import React, { useCallback, useEffect, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Link,
	Tooltip,
	Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import moment from 'moment/moment';

import { getQuotationClientLinks } from '../../services';
import { IQuotationClientLink, QuotationType } from '../../types';
import { buildClientProposalUrl, clientProposalNeedsIncognito } from '../../helpers';

interface Props {
	quotationId: number;
	open: boolean;
	onClose: () => void;
}

const QUOTATION_TYPE_LABEL: Record<number, string> = {
	[QuotationType.SIMPLEDESTINATION]: 'Un destino',
	[QuotationType.MULTIDESTINATION]: 'Multidestino',
	[QuotationType.ONLYFLIGHTS]: 'Solo vuelos',
};

const formatSentAt = (sentAt: string): string => {
	const parsed = moment(sentAt);
	return parsed.isValid() ? parsed.format('DD/MM/YY HH:mm') : 'Sin fecha';
};

export const SentClientLinksDialog = (props: Props): JSX.Element => {
	const { quotationId, open, onClose } = props;

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [links, setLinks] = useState<IQuotationClientLink[]>([]);
	const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);

	const fetchLinks = useCallback(async () => {
		try {
			setLoading(true);
			setError('');
			const response = await getQuotationClientLinks(quotationId);
			setLinks(response.data?.data?.links ?? []);
		} catch (err) {
			setError(
				err instanceof Error && err.message
					? err.message
					: 'No se pudieron traer las fotos enviadas de esta cotización.',
			);
			setLinks([]);
		} finally {
			setLoading(false);
		}
	}, [quotationId]);

	useEffect(() => {
		if (open) {
			fetchLinks();
		}
	}, [open, fetchLinks]);

	const handleCopy = async (linkId: string, url: string) => {
		try {
			await navigator.clipboard.writeText(url);
			setCopiedLinkId(linkId);
			setTimeout(() => setCopiedLinkId(null), 1800);
		} catch {
			setError('No se pudo copiar al portapapeles. Copiá la URL a mano.');
		}
	};

	const someLinkIsSameOrigin = links.some((link) => clientProposalNeedsIncognito(buildClientProposalUrl(link.linkId)));

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle sx={{ pb: 1 }}>
				<Typography component="span" variant="h6" fontWeight={700}>
					Fotos enviadas al cliente
				</Typography>
				<Typography variant="body2" color="text.secondary">
					Cotización n° {quotationId}
					{!loading && !error && links.length > 0 && ` · ${links.length} envío${links.length > 1 ? 's' : ''}`}
				</Typography>
			</DialogTitle>

			<DialogContent dividers>
				{loading && (
					<Box display="flex" alignItems="center" justifyContent="center" gap={1.5} py={4}>
						<CircularProgress size={22} />
						<Typography variant="body2" color="text.secondary">
							Buscando envíos…
						</Typography>
					</Box>
				)}

				{!loading && error && (
					<Alert severity="error" sx={{ mb: 1 }}>
						{error}
					</Alert>
				)}

				{!loading && !error && links.length === 0 && (
					<Box py={3} textAlign="center">
						<Typography variant="body2" color="text.secondary">
							Esta cotización todavía no se envió al cliente, así que no hay ninguna foto para ver.
						</Typography>
					</Box>
				)}

				{!loading && !error && links.length > 0 && (
					<>
						{someLinkIsSameOrigin && (
							<Alert severity="info" sx={{ mb: 2 }}>
								Los links apuntan a este mismo dominio. Si estás logueado en el Power, abrilos en una ventana de
								incógnito para verlos igual que el cliente.
							</Alert>
						)}

						<Box display="grid" gap={1.5}>
							{links.map((link) => {
								const url = buildClientProposalUrl(link.linkId);
								const copied = copiedLinkId === link.linkId;

								return (
									<Box
										key={link.linkId}
										sx={{
											border: '1px solid',
											borderColor: 'divider',
											borderRadius: '10px',
											p: 1.5,
										}}
									>
										<Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
											<Typography variant="body2" fontWeight={700}>
												{formatSentAt(link.sentAt)}
											</Typography>
											<Chip
												size="small"
												label={QUOTATION_TYPE_LABEL[link.quotationType] || `Tipo ${link.quotationType}`}
											/>
											{!link.wasSent && (
												<Tooltip title="Se generó el link pero no quedó registrado como enviado">
													<Chip size="small" color="warning" label="No enviada" />
												</Tooltip>
											)}
											<Box flex="1 1 auto" />
											<Typography variant="caption" color="text.secondary">
												{link.username || `Usuario ${link.userId}`}
											</Typography>
										</Box>

										{link.message && (
											<Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
												“{link.message}”
											</Typography>
										)}

										<Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
											<Link
												href={url}
												target="_blank"
												rel="noopener noreferrer"
												variant="caption"
												sx={{ wordBreak: 'break-all', flex: '1 1 auto' }}
											>
												{url}
											</Link>
											<Tooltip title={copied ? 'Link copiado' : 'Copiar link'}>
												<IconButton size="small" onClick={() => handleCopy(link.linkId, url)}>
													<ContentCopyIcon fontSize="small" color={copied ? 'success' : 'action'} />
												</IconButton>
											</Tooltip>
											<Tooltip title="Abrir en una pestaña nueva">
												<IconButton size="small" component="a" href={url} target="_blank" rel="noopener noreferrer">
													<OpenInNewIcon fontSize="small" />
												</IconButton>
											</Tooltip>
										</Box>
									</Box>
								);
							})}
						</Box>
					</>
				)}
			</DialogContent>

			<Divider />
			<DialogActions>
				{!loading && (
					<Button size="small" onClick={fetchLinks}>
						Actualizar
					</Button>
				)}
				<Button size="small" variant="contained" onClick={onClose}>
					Cerrar
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default SentClientLinksDialog;
